import {
  Component,
  ChangeDetectionStrategy,
  input,
  signal,
  computed,
  forwardRef,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { LucideAngularModule, LucideIconData, Eye, EyeOff, Lock, Mail } from 'lucide-angular';
import { FieldMessageComponent } from '../field-message/field-message.component';

/**
 * Text-like form input with optional icons, validation messaging, and masks.
 */
@Component({
  selector: 'app-field-input',
  imports: [LucideAngularModule, FieldMessageComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FieldInputComponent),
      multi: true,
    },
  ],
  templateUrl: './field-input.component.html',
  styleUrl: './field-input.component.css',
})
export class FieldInputComponent implements ControlValueAccessor {
  /** Field label. A trailing `*` marks the field as required. */
  readonly label        = input('');
  /** Native id assigned to the input element. */
  readonly id           = input('');
  /** Logical input type rendered by the component. */
  readonly type         = input<'text' | 'email' | 'password' | 'phone' | 'number' | 'tel' | 'url'>('text');
  /** Placeholder text shown when the value is empty. */
  readonly placeholder  = input('');
  /** Optional leading Lucide icon. */
  readonly icon         = input<LucideIconData | undefined>(undefined);
  /** Visual size preset. */
  readonly size         = input<'sm' | 'md'>('md');
  /** Optional character limit. */
  readonly maxLength    = input<number | null>(null);
  /** Optional minimum value for numeric inputs. */
  readonly min          = input<number | string | null>(null);
  /** Optional maximum value for numeric inputs. */
  readonly max          = input<number | string | null>(null);
  /** Optional step value for numeric inputs. */
  readonly step         = input<number | string | null>(null);
  /** Whether the field is currently invalid. */
  readonly invalid      = input(false);
  /** Validation error text. */
  readonly errorMessage = input('');
  /** Optional helper text shown below the control. */
  readonly hint         = input('');

  /** Current character count. */
  readonly charCount   = computed(() => this.value().length);
  /** Label text with any required marker removed. */
  readonly labelText   = computed(() => this.label().replace(/\s*\*\s*$/, '').trim());
  /** Whether the label includes a required marker. */
  readonly isRequired  = computed(() => this.label().includes('*'));
  /** Default Lucide icon used for common auth field types. */
  readonly defaultIcon = computed<LucideIconData | undefined>(() => {
    const t = this.type();
    if (t === 'email') return Mail;
    if (t === 'password') return Lock;
    return undefined;
  });
  /** Leading Lucide icon shown inside the field. */
  readonly leadingIcon = computed(() => this.icon() ?? this.defaultIcon());
  /** Whether the field has any leading icon treatment. */
  readonly hasLeadingIcon = computed(() => !!this.leadingIcon());

  /** Password-visible icon. */
  readonly EyeIcon    = Eye;
  /** Password-hidden icon. */
  readonly EyeOffIcon = EyeOff;

  /** Whether password inputs should be displayed as plain text. */
  readonly showPassword = signal(false);
  /** Current form value. */
  readonly value        = signal('');
  /** Disabled state received from Angular forms. */
  readonly isDisabled   = signal(false);

  /** Native input type after applying password, phone, and number behavior. */
  readonly inputType = computed(() => {
    const t = this.type();
    if (t === 'password') return this.showPassword() ? 'text' : 'password';
    if (t === 'phone') return 'tel';
    if (t === 'number') return 'text';
    return t;
  });
  /** Input mode used to show decimal keyboards for number fields. */
  readonly inputMode    = computed(() => this.type() === 'number' ? 'decimal' : null);
  /** Pattern used to constrain number-like text entry. */
  readonly inputPattern = computed(() => this.type() === 'number' ? '-?[0-9]*[.]?[0-9]*' : null);

  /** Registered Angular forms change callback. */
  private onChangeFn:  (v: string) => void = () => {};
  /** Registered Angular forms touched callback. */
  private onTouchedFn: () => void          = () => {};

  /**
   * Writes an external form value into the input.
   *
   * @param value Value supplied by Angular forms.
   */
  writeValue(value: string | number | null): void {
    const v = value == null ? '' : String(value);
    if (this.type() === 'phone') {
      this.value.set(this.maskPhone(v));
      return;
    }
    if (this.type() === 'number') {
      this.value.set(this.sanitizeNumber(v));
      return;
    }
    this.value.set(v);
  }

  /** @inheritdoc */
  registerOnChange(fn: (v: string) => void): void { this.onChangeFn = fn; }
  /** @inheritdoc */
  registerOnTouched(fn: () => void): void         { this.onTouchedFn = fn; }
  /** @inheritdoc */
  setDisabledState(isDisabled: boolean): void     { this.isDisabled.set(isDisabled); }

  /**
   * Handles native input changes, applying masks before emitting form updates.
   *
   * @param event Native input event.
   */
  onInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    let val = input.value;
    if (this.type() === 'phone') {
      val = this.maskPhone(val);
      input.value = val;
    } else if (this.type() === 'number') {
      val = this.sanitizeNumber(val);
      input.value = val;
    }
    this.value.set(val);
    this.onChangeFn(val);
    this.onTouchedFn();
  }

  /**
   * Marks the control as touched when focus leaves the input.
   */
  onBlur(): void { this.onTouchedFn(); }

  /**
   * Toggles password visibility for password inputs.
   */
  togglePassword(): void { this.showPassword.update(v => !v); }

  /**
   * Formats a string as a US phone number.
   *
   * @param raw Unformatted user input.
   * @returns Masked phone number with at most 10 digits.
   */
  private maskPhone(raw: string): string {
    const digits = raw.replace(/\D/g, '').slice(0, 10);
    if (!digits.length) return '';
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }

  /**
   * Removes unsupported characters from number-like input while preserving one decimal.
   *
   * @param raw Raw user input.
   * @returns Sanitized number string.
   */
  private sanitizeNumber(raw: string): string {
    let value = '';
    let hasDecimal = false;

    for (const char of raw) {
      if (/\d/.test(char)) {
        value += char;
      } else if (char === '-' && value === '') {
        value = '-';
      } else if (char === '.' && !hasDecimal) {
        value += '.';
        hasDecimal = true;
      }
    }

    return value;
  }
}
